import React, { useState, useEffect } from 'react';
import { X, Plane, Box, ShieldCheck, MapPin, DollarSign, Calculator, ArrowRight, Check, AlertCircle, Loader2, Sparkles, FileText, Plus, Trash2, RefreshCw, Globe, Truck, CheckCircle2, XCircle, User, Phone, UserCheck } from 'lucide-react';
import { PackageItem, UserAddress, ShipmentOrder, ShippingMethodOption, CustomDeclarationItem, UserProfile, ExtraService } from '../types';
import { calculateShippingCosts, SHIPPING_METHODS, DEFAULT_CURRENCY_RATES, isPackageFreeShipping } from '../lib/shippingAndPayment';
import { fetchAddressByCep, formatCep } from '../lib/viaCep';
import { formatCPF, validateCPF } from '../lib/cpfValidator';
import { ShippingSafetyMarginSuggestion } from './ShippingSafetyMarginSuggestion';
import { DESTINATION_COUNTRIES } from '../lib/countries';
import { ProFreeBoxesCounterBar } from './ProFreeBoxesCounterBar';
import { db } from '../lib/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

interface CheckoutConsolidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPackages: PackageItem[];
  userAddress?: UserAddress;
  suiteNumber: string;
  userId: string;
  userName: string;
  userEmail: string;
  onSubmitConsolidation: (shipment: Omit<ShipmentOrder, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void> | void;
  isAdmin?: boolean;
  currentUser?: UserProfile;
  shipments?: ShipmentOrder[];
}

export const CheckoutConsolidationModal: React.FC<CheckoutConsolidationModalProps> = ({
  isOpen,
  onClose,
  selectedPackages,
  userAddress,
  suiteNumber,
  userId,
  userName,
  userEmail,
  onSubmitConsolidation,
  isAdmin = false,
  currentUser,
  shipments = []
}) => {
  const [selectedMethodId, setSelectedMethodId] = useState<string>('ems');
  const [availableExtraServices, setAvailableExtraServices] = useState<ExtraService[]>([]);
  const [selectedExtraServiceIds, setSelectedExtraServiceIds] = useState<string[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const fetchServices = async () => {
        setLoadingServices(true);
        try {
          const q = query(collection(db, 'extra_services'), where('active', '==', true));
          const snap = await getDocs(q);
          const services = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as ExtraService));
          setAvailableExtraServices(services.sort((a, b) => a.price - b.price));
          // Auto-select free services by default
          setSelectedExtraServiceIds(services.filter(s => s.price === 0).map(s => s.id));
        } catch (e) {
          console.error('Failed to fetch extra services:', e);
        } finally {
          setLoadingServices(false);
        }
      };
      fetchServices();
    }
  }, [isOpen]);

  // Personal & Recipient Info State
  const [recipientName, setRecipientName] = useState(currentUser?.name || userName || '');
  const [recipientPhone, setRecipientPhone] = useState(currentUser?.phone || '');
  const [useProfileData, setUseProfileData] = useState(true);
  const [autoFillSuccess, setAutoFillSuccess] = useState(false);

  // Address Form State
  const [country, setCountry] = useState(currentUser?.address?.country || userAddress?.country || 'Brasil');
  const [street, setStreet] = useState(currentUser?.address?.street || userAddress?.street || '');
  const [number, setNumber] = useState(currentUser?.address?.number || userAddress?.number || '');
  const [complement, setComplement] = useState(currentUser?.address?.complement || userAddress?.complement || '');
  const [neighborhood, setNeighborhood] = useState(currentUser?.address?.neighborhood || userAddress?.neighborhood || '');
  const [city, setCity] = useState(currentUser?.address?.city || userAddress?.city || '');
  const [state, setState] = useState(currentUser?.address?.state || userAddress?.state || '');
  const [zipCode, setZipCode] = useState(currentUser?.address?.zipCode || userAddress?.zipCode || '');
  const [cpf, setCpf] = useState(currentUser?.address?.cpf || userAddress?.cpf || '');

  const [loading, setLoading] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepSuccessMsg, setCepSuccessMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [overrideWeightKg, setOverrideWeightKg] = useState<number | null>(null);

  const isBrazil = country === 'Brasil';

  // Function to automatically populate or restore personal data from registered profile
  const handleAutoFillFromProfile = (forceNotify = true) => {
    const profileAddr = currentUser?.address || userAddress;
    setRecipientName(currentUser?.name || userName || '');
    setRecipientPhone(currentUser?.phone || '');
    
    if (profileAddr) {
      if (profileAddr.country) setCountry(profileAddr.country);
      if (profileAddr.zipCode) setZipCode(profileAddr.zipCode);
      if (profileAddr.cpf) setCpf(profileAddr.cpf);
      if (profileAddr.street) setStreet(profileAddr.street);
      if (profileAddr.number) setNumber(profileAddr.number);
      if (profileAddr.complement !== undefined) setComplement(profileAddr.complement || '');
      if (profileAddr.neighborhood) setNeighborhood(profileAddr.neighborhood);
      if (profileAddr.city) setCity(profileAddr.city);
      if (profileAddr.state) setState(profileAddr.state);
    }
    setUseProfileData(true);
    if (forceNotify) {
      setAutoFillSuccess(true);
      setTimeout(() => setAutoFillSuccess(false), 3500);
    }
  };

  // Sync state when modal opens
  useEffect(() => {
    if (isOpen) {
      handleAutoFillFromProfile(false);
    }
  }, [isOpen, currentUser, userAddress]);

  // Customs Declaration State
  const [declarationItems, setDeclarationItems] = useState<CustomDeclarationItem[]>([]);
  const [selectedPackageForCustom, setSelectedPackageForCustom] = useState<string>('');
  const [customItemName, setCustomItemName] = useState<string>('');
  const [customItemValue, setCustomItemValue] = useState<string>('');

  useEffect(() => {
    if (selectedPackages && selectedPackages.length > 0) {
      setDeclarationItems(
        selectedPackages.map((p) => ({
          packageId: p.id,
          productName: p.description || `Produto (${p.store || 'Japão'})`,
          declaredValueYen: p.declaredValue || 0,
          quantity: 1
        }))
      );
    }
  }, [selectedPackages]);

  // Auto select Small Packet for free shipping items <= 2kg, or fallback to first eligible method
  useEffect(() => {
    if (!isOpen) return;
    const isAllFree = selectedPackages.length > 0 && selectedPackages.every(isPackageFreeShipping);
    const netWeight = selectedPackages.reduce((acc, p) => acc + (p.weightKg || 0), 0);
    const weight = overrideWeightKg !== null ? overrideWeightKg : netWeight;

    if (isAllFree && weight <= 2.0) {
      setSelectedMethodId('small_packet');
      return;
    }

    const calcs = calculateShippingCosts(weight, 1, currentUser?.subscriptionPlan || 'free', country, isAllFree);
    const current = calcs.find((c) => c.method.id === selectedMethodId);
    if (!current || !current.isEligible || current.method.isTemporarilyUnavailable) {
      const firstEligible = calcs.find((c) => c.isEligible && !c.method.isTemporarilyUnavailable);
      if (firstEligible) {
        setSelectedMethodId(firstEligible.method.id);
      }
    }
  }, [isOpen, selectedPackages, overrideWeightKg, country, currentUser]);

  const handleAddOrUpdateDeclaration = () => {
    if (!customItemName.trim() || !customItemValue) return;
    const val = parseFloat(customItemValue) || 0;

    if (selectedPackageForCustom) {
      setDeclarationItems((prev) => {
        const idx = prev.findIndex((item) => item.packageId === selectedPackageForCustom);
        if (idx >= 0) {
          return prev.map((item, i) => i === idx ? { ...item, productName: customItemName, declaredValueYen: val } : item);
        } else {
          return [...prev, { packageId: selectedPackageForCustom, productName: customItemName, declaredValueYen: val, quantity: 1 }];
        }
      });
    } else {
      setDeclarationItems((prev) => [
        ...prev,
        { productName: customItemName, declaredValueYen: val, quantity: 1 }
      ]);
    }

    setSelectedPackageForCustom('');
    setCustomItemName('');
    setCustomItemValue('');
  };

  const handleZipCodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (isBrazil) {
      const formatted = formatCep(raw);
      setZipCode(formatted);
      setCepSuccessMsg(null);

      const clean = formatted.replace(/\D/g, '');
      if (clean.length === 8) {
        setLoadingCep(true);
        const res = await fetchAddressByCep(clean);
        setLoadingCep(false);
        if (res) {
          if (res.street) setStreet(res.street);
          if (res.neighborhood) setNeighborhood(res.neighborhood);
          if (res.city) setCity(res.city);
          if (res.state) setState(res.state);
          setCepSuccessMsg(`Endereço localizado: ${res.city}/${res.state}`);
        }
      }
    } else {
      setZipCode(raw);
    }
  };

  if (!isOpen) return null;

  // Calculate Net Weights & Values
  const netWeightKg = selectedPackages.reduce((acc, p) => acc + (p.weightKg || 0), 0);
  const activeWeightKg = overrideWeightKg !== null ? overrideWeightKg : netWeightKg;
  const totalWeightKg = activeWeightKg;
  const totalWeightLb = totalWeightKg * 2.20462;
  const declaredTotalValueYen = selectedPackages.reduce((acc, p) => acc + (p.declaredValue || 0), 0);
  const totalDeclaredValueSum = declarationItems.reduce((acc, item) => acc + (item.declaredValueYen || 0), 0);
  const finalDeclaredTotalValue = totalDeclaredValueSum > 0 ? totalDeclaredValueSum : declaredTotalValueYen;

  // Check if all selected packages are Free Shipping
  const isAllFreeShipping = selectedPackages.length > 0 && selectedPackages.every(isPackageFreeShipping);

  // Calculate Shipping Costs using central lib (includes +25% for Japan Post)
  const shippingCalculations = calculateShippingCosts(activeWeightKg, 1, currentUser?.subscriptionPlan || 'free', country, isAllFreeShipping);
  const currentCalc = shippingCalculations.find((c) => c.method.id === selectedMethodId) || shippingCalculations[0];
  const isCourrierQuote = currentCalc.requiresQuote || currentCalc.method.requiresQuote;

  // Extra options cost
  const selectedServicesObjects = availableExtraServices.filter(s => selectedExtraServiceIds.includes(s.id));
  const extraServicesCostYen = selectedServicesObjects.reduce((acc, s) => acc + s.price, 0);

  const freightCostYen = (isAllFreeShipping || isCourrierQuote) ? 0 : currentCalc.costJpy;
  const totalCostYen = isAllFreeShipping ? 0 : (freightCostYen + extraServicesCostYen);
  const totalCostBrl = isAllFreeShipping ? 0 : (isCourrierQuote ? 0 : currentCalc.totalCostBrl);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipientName.trim()) {
      setError('Por favor, informe o Nome Completo do Destinatário.');
      return;
    }

    if (!street || !number || !city || !state || !zipCode || (isBrazil && !cpf)) {
      setError(`Por favor preencha todos os dados de endereço de entrega (${country})${isBrazil ? ', incluindo o CPF' : ''}.`);
      return;
    }

    if (isBrazil) {
      const cpfCheck = validateCPF(cpf);
      if (!cpfCheck.isValid) {
        setError(`CPF Inválido: ${cpfCheck.message}`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const destinationAddr: UserAddress = {
        street: street.trim(),
        number: number.trim(),
        complement: complement.trim(),
        neighborhood: neighborhood.trim(),
        city: city.trim(),
        state: state.trim(),
        zipCode: zipCode.trim(),
        cpf: cpf.trim(),
        country
      };

      await onSubmitConsolidation({
        suiteNumber,
        userId,
        clientName: userName,
        clientEmail: userEmail,
        clientPhone: recipientPhone.trim() || currentUser?.phone,
        recipientName: recipientName.trim() || userName,
        recipientPhone: recipientPhone.trim() || currentUser?.phone,
        packageIds: selectedPackages.map((p) => p.id),
        status: isCourrierQuote ? 'Aguardando Pagamento' : 'Aguardando Pagamento',
        shippingMethod: isAllFreeShipping ? 'FRETE GRÁTIS (Despacho Prioritário)' : currentCalc.method.name,
        totalWeightKg,
        totalWeightLb,
        estimatedShippingCost: isAllFreeShipping ? 0 : totalCostYen,
        shippingCostBrl: isAllFreeShipping ? 0 : totalCostBrl,
        declaredTotalValue: finalDeclaredTotalValue,
        customDeclarations: declarationItems,
        destinationAddress: destinationAddr,
        customOptions: {
          selectedExtraServices: selectedServicesObjects.map(s => ({
            id: s.id,
            name: s.name,
            price: s.price
          }))
        }
      });

      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao processar solicitação de envio.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm animate-fade-in p-3 sm:p-6">
      <div className="min-h-full flex items-start sm:items-center justify-center py-4 sm:py-8">
        <div className="bg-white rounded-3xl max-w-3xl w-full shadow-2xl overflow-hidden border border-slate-100 my-auto flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-600 to-blue-700 text-white p-5 sm:p-6 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-2xl border border-white/20">
              <Plane className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold">Solicitar Consolidação e Envio Internacional</h3>
              <p className="text-sky-100 text-xs sm:text-sm">
                Unindo {selectedPackages.length} {selectedPackages.length === 1 ? 'pacote' : 'pacotes'} na Suíte {suiteNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-6 overflow-y-auto flex-1">
          {isAllFreeShipping && (
            <div className="bg-gradient-to-r from-emerald-600 via-teal-700 to-emerald-800 text-white p-4.5 rounded-2xl shadow-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border border-emerald-400/60">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl flex-shrink-0 backdrop-blur-xs">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-black text-sm uppercase tracking-wider text-emerald-100 flex items-center gap-1.5">
                    <span>FRETE GRÁTIS INTERNACIONAL INCLUSO</span>
                  </h4>
                  <p className="text-xs text-emerald-100/90 font-medium mt-0.5">
                    Todos os produtos deste envio possuem o frete internacional pago. O valor do frete nesta consolidação é R$ 0,00!
                  </p>
                </div>
              </div>
              <div className="bg-white text-emerald-800 px-3.5 py-1.5 rounded-xl text-xs font-black font-mono shadow-xs self-end sm:self-center uppercase">
                FRETE GRÁTIS (R$ 0,00)
              </div>
            </div>
          )}

          {currentUser && (
            <ProFreeBoxesCounterBar
              currentUser={currentUser}
              shipments={shipments}
              compact
            />
          )}

          {error && (
            <div className="flex items-center gap-2.5 p-3.5 bg-red-50 text-red-700 rounded-xl text-xs sm:text-sm border border-red-200">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Selected Packages Summary */}
          <div>
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5 flex items-center gap-2">
              <Box className="w-4 h-4 text-sky-600" />
              1. Pacotes Selecionados para Consolidação
            </h4>
            <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
              {selectedPackages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 text-xs sm:text-sm"
                >
                  <div className="flex items-center gap-3">
                    <img 
                      src={pkg.photoUrl || 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=200&auto=format&fit=crop&q=80'} 
                      alt="" 
                      className="w-10 h-10 object-cover rounded-lg flex-shrink-0"
                    />
                    <div>
                      <p className="font-bold text-slate-800 line-clamp-1">{pkg.description}</p>
                      <p className="text-slate-500 text-[11px] font-mono">
                        {pkg.store}
                        {pkg.trackingIn && ` • ${pkg.trackingIn}`}
                        {pkg.barcode && ` • ${pkg.barcode}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 font-bold text-slate-700">
                    <div>{pkg.weightKg.toFixed(2)} kg / {pkg.weightLb.toFixed(2)} lb</div>
                    <div className="text-[11px] text-slate-400 font-mono">¥ {pkg.declaredValue.toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Combined Totals Bar */}
            <div className="mt-3 p-3.5 bg-sky-50 rounded-xl border border-sky-100 space-y-2 text-sky-950 font-bold text-xs sm:text-sm">
              <div className="flex flex-wrap justify-between items-center gap-2">
                <span>
                  Peso Líquido dos Itens: <strong className="text-sky-700">{netWeightKg.toFixed(2)} kg</strong>
                  {overrideWeightKg !== null && (
                    <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-900 text-[10px] font-extrabold rounded-full border border-amber-300">
                      Peso com Margem Aplicado: {overrideWeightKg.toFixed(2)} kg
                    </span>
                  )}
                </span>
                <span>Valor Declarado Total: <strong className="text-sky-700 font-mono">¥ {declaredTotalValueYen.toLocaleString()}</strong></span>
              </div>
              <div className="pt-2 border-t border-sky-200/60 flex items-center justify-between text-xs text-sky-900 font-semibold">
                <span>Taxa de Serviço de Redirecionamento ({totalWeightKg <= 1.0 ? 'Caixa até 1kg' : totalWeightKg <= 2.0 ? 'Caixa até 2kg' : 'Caixa acima de 2kg'}):</span>
                <div className="flex items-center gap-2">
                  <span className="font-extrabold text-sky-950 font-mono bg-white px-2 py-0.5 rounded border border-sky-200">
                    US$ {totalWeightKg <= 1.0 ? '15,00' : totalWeightKg <= 2.0 ? '20,00' : '30,00'} USD
                  </span>
                  {overrideWeightKg !== null && (
                    <button
                      type="button"
                      onClick={() => setOverrideWeightKg(null)}
                      className="text-[10px] text-sky-700 underline font-bold hover:text-sky-900"
                      title="Voltar para o peso líquido sem margem de segurança"
                    >
                      (Restaurar Peso Líquido)
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Automatic Shipping Safety Margin Suggestion */}
          <ShippingSafetyMarginSuggestion
            netWeightKg={netWeightKg}
            destinationCountry={country}
            isFreeShipping={isAllFreeShipping}
            onApplySuggestedWeight={(suggestedGross, recommendedMethodId) => {
              setOverrideWeightKg(suggestedGross);
              if (recommendedMethodId) {
                setSelectedMethodId(recommendedMethodId);
              }
            }}
            isAdmin={isAdmin}
          />

          {/* Step 2: Shipping Method Selector */}
          <div>
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5 flex items-center gap-2">
              <Plane className="w-4 h-4 text-sky-600" />
              2. Escolha o Método de Frete Internacional
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {shippingCalculations.map((calc) => {
                const isSelected = selectedMethodId === calc.method.id;
                const isQuoteReq = calc.requiresQuote || calc.method.requiresQuote;
                const isDisabled = !calc.isEligible || calc.method.isTemporarilyUnavailable;

                return (
                  <div
                    key={calc.method.id}
                    onClick={() => {
                      if (!isDisabled) {
                        setSelectedMethodId(calc.method.id);
                      }
                    }}
                    className={`p-3.5 rounded-2xl border transition-all ${
                      isDisabled
                        ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'
                        : isSelected
                        ? 'bg-sky-50/60 border-sky-500 ring-2 ring-sky-500/20 shadow-sm cursor-pointer'
                        : 'bg-white border-slate-200 hover:border-slate-300 cursor-pointer'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="font-bold text-slate-800 text-xs sm:text-sm flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{calc.method.name}</span>
                          {calc.method.isTemporarilyUnavailable && (
                            <span className="px-1.5 py-0.5 bg-rose-100 text-rose-800 text-[9px] font-black rounded uppercase">
                              Indisponível p/ Brasil
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                        isSelected && !isDisabled ? 'border-sky-600 bg-sky-600 text-white' : 'border-slate-300 bg-slate-100'
                      }`}>
                        {isSelected && !isDisabled && <Check className="w-3 h-3 stroke-[3]" />}
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Prazo estimado: {calc.method.estimatedDays}</p>
                    
                    {isDisabled ? (
                      <div className="mt-2 text-[10px] font-bold text-rose-600 flex items-center gap-1 bg-rose-50 p-2 rounded-lg border border-rose-100/80">
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 text-rose-500" />
                        <span>{calc.ineligibilityReason}</span>
                      </div>
                    ) : isAllFreeShipping ? (
                      <div className="mt-2 text-xs font-black text-emerald-600 font-mono flex items-center gap-1">
                        <Truck className="w-3.5 h-3.5" />
                        <span>FRETE GRÁTIS (R$ 0,00)</span>
                      </div>
                    ) : isQuoteReq ? (
                      <div className="mt-2 text-xs font-extrabold text-purple-700 font-sans">
                        Sob Cotação (Definido pelo CEP abaixo)
                      </div>
                    ) : (
                      <div className="mt-2 text-xs font-extrabold text-slate-900 font-mono">
                        ¥ {calc.costJpy.toLocaleString()} <span className="text-slate-500 font-sans font-normal">(~ R$ {calc.totalCostBrl.toFixed(2)})</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step 3: Custom Packaging Options */}
          {!isAllFreeShipping && (
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-sky-600" />
                3. Serviços Adicionais de Depósito
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-semibold text-slate-700">
                
                {loadingServices ? (
                  <div className="col-span-full p-4 text-center text-slate-500 text-sm">Carregando serviços...</div>
                ) : availableExtraServices.length === 0 ? (
                  <div className="col-span-full p-4 text-center text-slate-500 text-sm bg-slate-50 rounded-xl">
                    Nenhum serviço extra disponível no momento.
                  </div>
                ) : (
                  availableExtraServices.map(service => (
                    <label key={service.id} className="flex items-center gap-2.5 p-3 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100/80 transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedExtraServiceIds.includes(service.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedExtraServiceIds(prev => [...prev, service.id]);
                          } else {
                            setSelectedExtraServiceIds(prev => prev.filter(id => id !== service.id));
                          }
                        }}
                        className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500"
                      />
                      <div>
                        <span className="block font-bold">
                          {service.name} {service.price > 0 ? `(+¥${service.price.toLocaleString('ja-JP')})` : '(Grátis)'}
                        </span>
                        {service.description && (
                          <span className="text-[11px] text-slate-500">{service.description}</span>
                        )}
                      </div>
                    </label>
                  ))
                )}

              </div>
            </div>
          )}

          {/* Step 4: Personal Information & Destination Address Form */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-2">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <MapPin className="w-4 h-4 text-sky-600" />
                {isAllFreeShipping ? '3. Informações Pessoais do Envio & Endereço' : '4. Informações Pessoais do Envio & Endereço'}
              </h4>
              <span className="text-[10px] font-bold text-sky-700 bg-sky-50 px-2 py-0.5 rounded border border-sky-200/60 self-start sm:self-auto">
                {DESTINATION_COUNTRIES.find(c => c.name === country)?.flag || '🌐'} {country}
              </span>
            </div>

            {/* Auto-fill from Profile Card */}
            <div className="bg-gradient-to-r from-sky-50/80 via-blue-50/50 to-indigo-50/70 border border-sky-200/80 rounded-2xl p-3.5 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                <label className="flex items-start sm:items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={useProfileData}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setUseProfileData(checked);
                      if (checked) {
                        handleAutoFillFromProfile(true);
                      }
                    }}
                    className="w-4 h-4 mt-0.5 sm:mt-0 rounded text-sky-600 focus:ring-sky-500 border-slate-300"
                  />
                  <div>
                    <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <UserCheck className="w-3.5 h-3.5 text-sky-600" />
                      Preenchimento automático com dados cadastrais
                    </span>
                    <p className="text-[11px] text-slate-500">
                      Preenche automaticamente com o Nome, Telefone, CPF e Endereço salvos no seu cadastro.
                    </p>
                  </div>
                </label>

                <button
                  type="button"
                  onClick={() => handleAutoFillFromProfile(true)}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-sky-700 bg-white hover:bg-sky-100/70 border border-sky-300/80 rounded-xl transition-all shadow-sm active:scale-95 shrink-0"
                  title="Carregar ou restaurar dados do seu perfil cadastrado"
                >
                  <Sparkles className="w-3.5 h-3.5 text-sky-600" />
                  <span>Usar Meus Dados</span>
                </button>
              </div>

              {autoFillSuccess && (
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 bg-emerald-100/70 border border-emerald-200 px-3 py-1.5 rounded-xl animate-fade-in">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Dados cadastrais aplicados com sucesso! Você pode editá-los se desejar enviar para outro endereço.</span>
                </div>
              )}
            </div>

            {/* Recipient Personal Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl">
              <div>
                <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-sky-600" />
                  <span>Nome Completo do Destinatário *</span>
                </label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Nome de quem vai receber a encomenda"
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-xs font-semibold focus:ring-2 focus:ring-sky-500"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-sky-600" />
                  <span>Telefone / WhatsApp de Contato</span>
                </label>
                <input
                  type="text"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="Ex: +55 11 99999-9999"
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-xs font-semibold focus:ring-2 focus:ring-sky-500"
                />
              </div>
            </div>

            {/* Country Selector */}
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-blue-600" />
                <span>País de Destino da Caixa</span>
              </label>
              <select
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  setCepSuccessMsg(null);
                }}
                className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-800 focus:ring-2 focus:ring-sky-500"
              >
                {DESTINATION_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.name}>
                    {c.flag} {c.name}
                  </option>
                ))}
              </select>
              {(() => {
                const cInfo = DESTINATION_COUNTRIES.find(c => c.name === country) || DESTINATION_COUNTRIES[0];
                return (
                  <p className="text-[10px] text-sky-800 font-bold mt-1 flex items-center gap-1.5 bg-sky-50 px-2.5 py-1 rounded-md border border-sky-100">
                    <Globe className="w-3 h-3 text-sky-600 flex-shrink-0" />
                    <span>Tabela de Frete: <strong>{cInfo.shippingZone}</strong> (Valores atualizados em tempo real)</span>
                  </p>
                );
              })()}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1 flex items-center justify-between">
                  <span>{isBrazil ? 'CEP *' : 'Código Postal / ZIP Code *'}</span>
                  {loadingCep && <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600 inline" />}
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={handleZipCodeChange}
                  placeholder={isBrazil ? "01000-000" : "1000-001"}
                  maxLength={isBrazil ? 9 : 20}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold font-mono focus:ring-2 focus:ring-emerald-500"
                  required
                />
                {cepSuccessMsg && (
                  <p className="text-[10px] text-emerald-700 font-bold mt-1 flex items-center gap-1">
                    <Check className="w-3 h-3 text-emerald-600" />
                    {cepSuccessMsg}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                  {isBrazil ? 'CPF (Alfândega - Receita Federal) *' : 'Passaporte / Tax ID'}
                </label>
                <input
                  type="text"
                  value={cpf}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCpf(isBrazil ? formatCPF(val) : val);
                  }}
                  placeholder={isBrazil ? "000.000.000-00" : "Passaporte / Tax ID"}
                  maxLength={isBrazil ? 14 : 30}
                  className={`w-full px-3 py-2 rounded-xl border text-xs font-semibold font-mono transition-all ${
                    isBrazil && cpf
                      ? validateCPF(cpf).isValid
                        ? 'border-emerald-500 bg-emerald-50/20 text-emerald-950 focus:ring-2 focus:ring-emerald-500'
                        : 'border-rose-400 bg-rose-50/20 text-rose-950 focus:ring-2 focus:ring-rose-400'
                      : 'border-slate-200 focus:ring-2 focus:ring-sky-500'
                  }`}
                  required={isBrazil}
                />
                {isBrazil && cpf && (
                  <div className="mt-1">
                    {validateCPF(cpf).isValid ? (
                      <p className="text-[10px] font-extrabold text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span>CPF Autêntico & Válido (Receita Federal)</span>
                      </p>
                    ) : (
                      <p className="text-[10px] font-extrabold text-rose-600 flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                        <span>{validateCPF(cpf).message}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Bairro / Distrito</label>
                <input
                  type="text"
                  value={neighborhood}
                  onChange={(e) => setNeighborhood(e.target.value)}
                  placeholder="Ex: Ipanema / District"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Rua / Logradouro *</label>
                <input
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="Ex: Rua Visconde de Pirajá / Street"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Número *</label>
                <input
                  type="text"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder="Ex: 420"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Complemento</label>
                <input
                  type="text"
                  value={complement}
                  onChange={(e) => setComplement(e.target.value)}
                  placeholder="Ex: Apto 102"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                  {isBrazil ? 'Cidade / UF *' : 'Cidade / Estado / Província *'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Cidade / City"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold"
                    required
                  />
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder={isBrazil ? "RJ" : "Estado/Província"}
                    className="w-28 px-2 py-2 rounded-xl border border-slate-200 text-xs font-semibold uppercase text-center"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Step 5: Customs Declaration / Product Value Section */}
          <div className="pt-2">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5 flex items-center gap-2">
              <FileText className="w-4 h-4 text-sky-600" />
              {isAllFreeShipping ? '4. Declaração de Conteúdo e Valor de Remessa (Alfândega)' : '5. Declaração de Conteúdo e Valor de Remessa (Alfândega)'}
            </h4>

            <p className="text-xs text-slate-500 mb-3">
              Selecione o produto/pacote abaixo e ajuste a descrição e o valor individual a ser declarado na caixa para a alfândega e remessa internacional.
            </p>

            {/* Custom Item Selector Form */}
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl mb-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                    Selecionar Produto / Pacote
                  </label>
                  <select
                    value={selectedPackageForCustom}
                    onChange={(e) => {
                      const pkgId = e.target.value;
                      setSelectedPackageForCustom(pkgId);
                      const found = selectedPackages.find((p) => p.id === pkgId);
                      if (found) {
                        setCustomItemName(found.description);
                        setCustomItemValue(found.declaredValue.toString());
                      }
                    }}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-medium bg-white focus:ring-2 focus:ring-sky-500"
                  >
                    <option value="">-- Escolha um produto da caixa --</option>
                    {selectedPackages.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.description} ({p.store}) - ¥{p.declaredValue.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                    Descrição para Declaração
                  </label>
                  <input
                    type="text"
                    value={customItemName}
                    onChange={(e) => setCustomItemName(e.target.value)}
                    placeholder="Ex: Brinquedo Plástico / Vestuário"
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-medium bg-white focus:ring-2 focus:ring-sky-500"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                    Valor Declarado (¥ JPY)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      value={customItemValue}
                      onChange={(e) => setCustomItemValue(e.target.value)}
                      placeholder="Ex: 2500"
                      className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-bold font-mono bg-white focus:ring-2 focus:ring-sky-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddOrUpdateDeclaration}
                      className="px-3 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl text-xs font-bold shrink-0 flex items-center gap-1 shadow-xs transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Aplicar</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Declaration Items List Table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
              <div className="bg-slate-100 px-3 py-2 border-b border-slate-200 flex flex-wrap justify-between items-center gap-2 text-xs font-bold text-slate-700">
                <span>Itens Declarados na Remessa da Caixa</span>
                <span className="text-slate-600">
                  Total Declarado: <strong className="text-emerald-700 font-mono font-extrabold text-xs">¥ {finalDeclaredTotalValue.toLocaleString()}</strong>
                  <span className="text-[11px] text-slate-500 font-normal ml-1">
                    (~ R$ {(finalDeclaredTotalValue * DEFAULT_CURRENCY_RATES.jpyToBrl).toLocaleString('pt-BR', { minimumFractionDigits: 2 })})
                  </span>
                </span>
              </div>
              <div className="divide-y divide-slate-100 text-xs">
                {declarationItems.map((item, idx) => (
                  <div key={idx} className="p-2.5 flex items-center justify-between gap-3 hover:bg-slate-50">
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={item.productName}
                        onChange={(e) => {
                          const newName = e.target.value;
                          setDeclarationItems((prev) => prev.map((it, i) => i === idx ? { ...it, productName: newName } : it));
                        }}
                        className="font-bold text-slate-800 w-full bg-transparent border-b border-transparent hover:border-slate-300 focus:border-sky-500 outline-none text-xs"
                      />
                      <span className="text-[10px] text-slate-400 block truncate">
                        Qtd: {item.quantity || 1} {item.packageId ? `• Pacote Origem: ${selectedPackages.find(p => p.id === item.packageId)?.store || 'Japão'}` : ''}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-center gap-1 bg-slate-100 px-2.5 py-1.5 rounded-lg border border-slate-200">
                        <span className="text-slate-500 font-bold text-xs">¥</span>
                        <input
                          type="number"
                          min="0"
                          value={item.declaredValueYen}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setDeclarationItems((prev) => prev.map((it, i) => i === idx ? { ...it, declaredValueYen: val } : it));
                          }}
                          className="w-20 bg-transparent font-mono font-bold text-slate-800 text-xs outline-none text-right"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDeclarationItems((prev) => prev.filter((_, i) => i !== idx))}
                        className="p-1.5 text-slate-400 hover:text-rose-600 rounded-md transition-colors"
                        title="Remover item da declaração"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                {declarationItems.length === 0 && (
                  <div className="p-4 text-center text-slate-400 text-xs italic">
                    Nenhum item na declaração. Selecione um produto acima para adicionar.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pricing Summary & Action */}
          <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <span className="text-xs text-slate-500 font-semibold block">Total Estimado da Consolidação:</span>
              <div className="flex items-baseline gap-2">
                {isAllFreeShipping ? (
                  <div>
                    <span className="text-xl sm:text-2xl font-black text-emerald-600 font-mono flex items-center gap-1.5">
                      <Truck className="w-5 h-5 text-emerald-600" />
                      FRETE GRÁTIS
                    </span>
                    <span className="text-xs font-extrabold text-emerald-700 block">
                      Valor do Frete Internacional: R$ 0,00
                    </span>
                  </div>
                ) : isCourrierQuote ? (
                  <div>
                    <span className="text-base sm:text-lg font-black text-purple-800">
                      Sob Cotação Privada com CEP
                    </span>
                    <span className="text-[11px] text-slate-500 block font-normal">
                      Nossa equipe enviará a cotação exata das transportadoras após análise do CEP.
                    </span>
                  </div>
                ) : (
                  <>
                    <span className="text-xl sm:text-2xl font-black text-slate-900 font-mono">
                      ¥ {totalCostYen.toLocaleString()}
                    </span>
                    <span className="text-sm font-extrabold text-emerald-600">
                      (~ R$ {totalCostBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-slate-600 font-semibold text-xs sm:text-sm hover:bg-slate-100 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-700 hover:to-blue-800 text-white font-bold text-sm rounded-xl shadow-lg shadow-sky-500/25 transition-all w-full sm:w-auto disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Confirmar Solicitação de Envio</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>

        </form>
      </div>
    </div>
  </div>
  );
};

export default CheckoutConsolidationModal;
